import { ENDCARD_FEED_MSG } from "@config/QuickNewsConstants";
import {
  loadLottieAnimation,
  type LottieConfig,
  type LottieInstance,
} from "@helper/lottieHelper";
import { FinishCardProps } from "@typings/QuickNews";
import { ReactElement, useEffect, useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";

type QNEndCardProps = {
  item: FinishCardProps;
  getOlderArticles?: () => void;
};

export function QnEndCard({
  item,
  getOlderArticles,
}: QNEndCardProps): ReactElement {
  const endCardInstanceRef = useRef<LottieInstance | null>(null);
  const readMoreInstanceRef = useRef<LottieInstance | null>(null);

  // Stable IDs, will not recreate on every render
  const { endCardCanvasId, readMoreCanvasId } = useMemo(
    () => ({
      endCardCanvasId: `end-card-canvas`,
      readMoreCanvasId: `qn-end-card-read-more-${item.id}`,
    }),
    [item.id],
  );

  useEffect(() => {
    let isMounted = true;

    const loadAnimations = () => {
      // End card animation
      const endCardConfig: LottieConfig = {
        animationPath: "quick-news/endCard.lottie",
        canvasId: endCardCanvasId,
        dimensions: { width: 300, height: 300, minHeight: 300 },
      };

      const endCardInstance = loadLottieAnimation(endCardConfig);
      if (isMounted) endCardInstanceRef.current = endCardInstance;

      // Read more animation (only for MyFeed)

      const readMoreConfig: LottieConfig = {
        animationPath: "quick-news/readMore.lottie",
        canvasId: readMoreCanvasId,
        dimensions: { height: 29, minHeight: 29 },
      };

      const readMoreInstance = loadLottieAnimation(readMoreConfig);
      if (isMounted) readMoreInstanceRef.current = readMoreInstance;
    };

    loadAnimations();

    return () => {
      isMounted = false;

      endCardInstanceRef.current?.destroy();
      endCardInstanceRef.current = null;

      readMoreInstanceRef.current?.destroy();
      readMoreInstanceRef.current = null;
    };
  }, [endCardCanvasId, readMoreCanvasId]);

  return (
    <div className="w-full h-full bg-black/30" key={item.id}>
      <div className="grid grid-rows-5 h-full">
        {/* Animation Canvas */}
        <div className="row-span-3 flex items-center justify-center">
          <canvas id={endCardCanvasId} className="m-auto" />
        </div>

        {/* Text Message */}
        <div className="row-span-2 text-center mx-auto w-[60%] text-yellow-100 font-bold">
          <div className="text-5xl leading-[120%] font-primary">Well Done!</div>
          <div className="text-xl leading-[150%] font-opensans">
            {ENDCARD_FEED_MSG}
          </div>
        </div>
      </div>

      {/* Read More Button for MyFeed */}
      <button
        aria-label="Next card"
        className="absolute bottom-4 w-full text-white flex flex-col items-center justify-center"
        onClick={getOlderArticles}
      >
        Swipe for more news
        <canvas id={readMoreCanvasId} className={twMerge("mx-auto w-[11px]")} />
      </button>
    </div>
  );
}
